import {
  Perfil as SchemaDoPerfil,
  TIPOS_DE_PERFIL,
  type Perfil,
  type TipoDePerfil,
} from '@sheafyield/dominio';
import { useRef, useState } from 'react';

import type { DonoDasLinhas } from '../dados/dono.js';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { Selecao } from '../ds/Selecao.js';
import { Toast } from '../ds/Toast.js';
import './ajustes.css';

const ROTULOS: Record<TipoDePerfil, string> = {
  publicador: 'Publicador',
  nao_batizado: 'Não batizado',
  auxiliar: 'Pioneiro auxiliar',
  regular: 'Pioneiro regular',
  especial: 'Pioneiro especial',
  missionario: 'Missionário',
};

export const opcoesDePerfil = TIPOS_DE_PERFIL.map((valor) => ({
  valor,
  rotulo: ROTULOS[valor],
}));

const TIPOS_QUE_RELATAM_HORAS: ReadonlySet<TipoDePerfil> = new Set([
  'auxiliar',
  'regular',
  'especial',
  'missionario',
]);

const BASE_DO_PADRAO = {
  id: '00000000-0000-4000-8000-000000000000',
  user_id: '00000000-0000-4000-8000-000000000000',
  updated_at: '2026-01-01T00:00:00.000Z',
  server_updated_at: null,
  deleted: false,
  nome_exibicao: '',
} as const;

type EntradaDoPerfil = Pick<
  Perfil,
  'tipo' | 'meta_horas_mes' | 'nome_exibicao'
>;

type Props = {
  /** `undefined` enquanto a leitura da identidade atual ainda não voltou. */
  perfil: Perfil | undefined;
  /** Trocar dono invalida o rascunho, mesmo que o perfil anterior pareça igual. */
  identidade: string;
  salvar: (identidade: string, entrada: EntradaDoPerfil) => Promise<Perfil>;
};

const mensagemDe = (causa: unknown) =>
  causa instanceof Error
    ? causa.message
    : 'Não deu para salvar o perfil. Tente novamente.';

function ehTipoDePerfil(valor: string): valor is TipoDePerfil {
  return (TIPOS_DE_PERFIL as readonly string[]).includes(valor);
}

function metaPadrao(tipo: TipoDePerfil): number {
  // O padrão pertence ao schema do domínio. A tela omite a meta ao validá-lo
  // para não manter uma segunda tabela de metas que pudesse divergir dele.
  return SchemaDoPerfil.parse({ ...BASE_DO_PADRAO, tipo }).meta_horas_mes;
}

function rascunhoDe(perfil: Perfil): EntradaDoPerfil {
  return {
    tipo: perfil.tipo,
    meta_horas_mes: perfil.meta_horas_mes,
    nome_exibicao: perfil.nome_exibicao,
  };
}

function seloDoPerfil(perfil: Perfil | undefined): string {
  if (perfil === undefined) return 'carregando';
  // O perfil virtual ganha um carimbo novo em cada leitura. O formulário só
  // edita estes três campos: incluir `updated_at` aqui apagaria o rascunho a
  // cada releitura, embora nada que ele mostra tenha mudado.
  return JSON.stringify(rascunhoDe(perfil));
}

/** Só entrega o dono à escrita se ainda for o da tela que iniciou o toque. */
export async function salvarParaIdentidade<T>(
  identidade: string,
  donoAtual: DonoDasLinhas,
  salvar: (dono: DonoDasLinhas) => Promise<T>,
): Promise<T> {
  return salvar(async () => {
    const atual = await donoAtual();
    if (atual !== identidade)
      throw new Error('A conta mudou. Confira o perfil antes de salvar.');
    return atual;
  });
}

export function PerfilEMeta({ perfil, identidade, salvar }: Props) {
  const selo = seloDoPerfil(perfil);
  const [origem, setOrigem] = useState({ identidade, selo });
  const [rascunho, setRascunho] = useState<EntradaDoPerfil | null>(() =>
    perfil === undefined ? null : rascunhoDe(perfil),
  );
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmado, setConfirmado] = useState<EntradaDoPerfil | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emCurso = useRef<string | null>(null);

  // Identidade nova é uma fronteira de privacidade: um formulário começado
  // sem conta não pode virar uma alteração da conta que acabou de entrar.
  // O reset durante a renderização evita um quadro com os dados anteriores.
  if (origem.identidade !== identidade || origem.selo !== selo) {
    const mesmaConfirmacao =
      origem.identidade === identidade &&
      confirmado !== null &&
      selo === JSON.stringify(confirmado);
    setOrigem({ identidade, selo });
    setRascunho(perfil === undefined ? null : rascunhoDe(perfil));
    setErro(null);
    if (!mesmaConfirmacao) {
      setAviso(null);
      setConfirmado(null);
    }
    setSalvando(false);
  }

  function selecionarTipo(valor: string) {
    if (!ehTipoDePerfil(valor) || rascunho === null) return;
    setErro(null);
    setAviso(null);
    setRascunho({
      ...rascunho,
      tipo: valor,
      meta_horas_mes: metaPadrao(valor),
    });
  }

  async function aoSalvar() {
    if (rascunho === null || emCurso.current === identidade) return;
    emCurso.current = identidade;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await salvar(identidade, {
        ...rascunho,
        nome_exibicao: rascunho.nome_exibicao.trim(),
      });
      if (emCurso.current === identidade) {
        setConfirmado({
          ...rascunho,
          nome_exibicao: rascunho.nome_exibicao.trim(),
        });
        setAviso('Perfil salvo.');
      }
    } catch (causa) {
      if (emCurso.current === identidade) setErro(mensagemDe(causa));
    } finally {
      if (emCurso.current === identidade) {
        emCurso.current = null;
        setSalvando(false);
      }
    }
  }

  if (rascunho === null) {
    return (
      <section className="ajustes__secao" aria-busy="true">
        <h2>Perfil e meta</h2>
        <p>Carregando perfil…</p>
      </section>
    );
  }

  const relataHoras = TIPOS_QUE_RELATAM_HORAS.has(rascunho.tipo);
  return (
    <section className="ajustes__secao ajustes__perfil">
      <h2>Perfil e meta</h2>
      <form
        onSubmit={(evento) => {
          evento.preventDefault();
          void aoSalvar();
        }}
      >
        <fieldset className="ajustes__perfil-campos" disabled={salvando}>
          <Selecao
            rotulo="Perfil"
            valor={rascunho.tipo}
            opcoes={opcoesDePerfil}
            aoMudar={selecionarTipo}
          />
          {relataHoras ? (
            <Campo
              rotulo="Meta de horas no mês"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={String(rascunho.meta_horas_mes)}
              onChange={(evento) => {
                const meta = Number(evento.target.value);
                if (!Number.isInteger(meta) || meta < 0) return;
                setErro(null);
                setAviso(null);
                setRascunho({ ...rascunho, meta_horas_mes: meta });
              }}
            />
          ) : null}
          <Campo
            rotulo="Nome de exibição"
            dica="Opcional"
            value={rascunho.nome_exibicao}
            onChange={(evento) => {
              setErro(null);
              setAviso(null);
              setRascunho({ ...rascunho, nome_exibicao: evento.target.value });
            }}
          />
        </fieldset>
        {erro === null ? null : (
          <p className="ajustes__erro" role="alert">
            {erro}
          </p>
        )}
        <Botao type="submit" largura="cheia" carregando={salvando}>
          Salvar perfil
        </Botao>
      </form>
      <Toast mensagem={aviso} chave={aviso} />
    </section>
  );
}
